import type { UploadFile } from 'antd/es/upload/interface';
import { AxiosInstance } from 'axios';

import config from '../../config';
import { UPLOAD_TYPES } from '../../constants';

import { getAccessTokenClient, getAttendeeClient } from '../client/http';

const {
  apiBaseUrl: { baseApi },
} = config;

const accessTokenClient = () => getAccessTokenClient(baseApi);
const attendeeClient = () => getAttendeeClient(baseApi);

interface UploaderOptions {
  chunkSize?: number;
  threadsQuantity?: number;
  file: UploadFile;
  fileId: string;
  isPublic?: boolean;
}

interface AWSPart {
  PartNumber: number;
  signedUrl: string;
}

interface UploadedPart {
  PartNumber: number;
  ETag: string;
}

interface ProgressData {
  sent: number;
  total: number;
  percentage: number;
}

export class Uploader {
  private chunkSize: number;
  private threadsQuantity: number;
  private file: UploadFile;
  private fileId: string;
  private aborted: boolean;
  private uploadedSize: number;
  private progressCache: Record<number, number>;
  private activeConnections: Record<number, XMLHttpRequest>;
  private parts: AWSPart[];
  private uploadedParts: UploadedPart[];
  private uploadId: string | null;
  private fileKey: string | null;
  private onProgressFn: (progress: ProgressData) => void;
  private onErrorFn: (error: Error) => void;
  private onCompleteFn: () => void;
  private client: AxiosInstance;

  constructor(options: UploaderOptions) {
    this.chunkSize = options.chunkSize || 1024 * 1024 * 5;
    this.threadsQuantity = Math.min(options.threadsQuantity || 5, 15);
    this.file = options.file;
    this.fileId = options.fileId;
    this.aborted = false;
    this.uploadedSize = 0;
    this.progressCache = {};
    this.activeConnections = {};
    this.parts = [];
    this.uploadedParts = [];
    this.uploadId = null;
    this.fileKey = null;
    this.onProgressFn = () => {};
    this.onErrorFn = () => {};
    this.onCompleteFn = () => {};
    this.client = options.isPublic ? attendeeClient() : accessTokenClient();
  }

  start() {
    this.initialize();
  }

  async initialize() {
    try {
      const fileName = (this.file.name || '').replace(/'/g, '');

      const videoInitializationUploadInput = { fileName, fileId: this.fileId };

      const initializeReponse = await this.client.post(
        '/api/upload/initialise',
        videoInitializationUploadInput,
      );

      const AWSFileDataOutput = initializeReponse.data;
      this.uploadId = AWSFileDataOutput.uploadId;
      this.fileKey = AWSFileDataOutput.fileKey;

      const numberOfParts = Math.ceil((this.file.size || 0) / this.chunkSize);

      const AWSMultipartFileDataInput = {
        uploadId: this.uploadId,
        fileKey: this.fileKey,
        parts: numberOfParts,
      };

      const urlsResponse = await this.client.post(
        '/api/upload/generate-urls',
        AWSMultipartFileDataInput,
      );

      const newParts = urlsResponse.data.parts;
      this.parts.push(...newParts);
      this.sendNext();
    } catch (error) {
      await this.complete(error as Error);
    }
  }

  sendNext() {
    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) return;

    if (!this.parts.length) {
      if (!activeConnections) this.complete();
      return;
    }

    const part = this.parts.pop();
    if (!part) return;

    const fileBlob = (this.file.originFileObj || this.file) as any as Blob;

    const sentSize = (part.PartNumber - 1) * this.chunkSize;
    const chunk = fileBlob.slice(sentSize, sentSize + this.chunkSize);

    const sendChunkStarted = () => {
      this.sendNext();
    };

    this.sendChunk(chunk, part, sendChunkStarted)
      .then(() => {
        this.sendNext();
      })
      .catch((error) => {
        this.parts.push(part);
        this.complete(error);
      });
  }

  async complete(error?: Error) {
    if (error) {
      this.onErrorFn(error);
      return;
    }

    try {
      await this.sendCompleteRequest();
    } catch (error) {
      this.onErrorFn(error as Error);
    }
  }

  async sendCompleteRequest() {
    if (this.uploadId && this.fileKey) {
      const videoFinalizationMultiPartInput = {
        uploadId: this.uploadId,
        fileKey: this.fileKey,
        parts: this.uploadedParts,
      };

      await this.client.post(
        '/api/upload/finalise',
        videoFinalizationMultiPartInput,
      );

      this.onCompleteFn();
    }
  }

  sendChunk(chunk: Blob, part: AWSPart, sendChunkStarted: () => void) {
    return new Promise<void>((resolve, reject) => {
      this.upload(chunk, part, sendChunkStarted)
        .then((status) => {
          if (status !== 200) {
            reject(new Error('Failed chunk upload'));
            return;
          }
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  handleProgress(part: number, event: any) {
    if (!this.file) return;

    if (
      event.type === UPLOAD_TYPES.PROGRESS ||
      event.type === UPLOAD_TYPES.ERROR ||
      event.type === UPLOAD_TYPES.ABORT
    )
      this.progressCache[part] = event.loaded;

    if (event.type === UPLOAD_TYPES.UPLOADED) {
      this.uploadedSize += this.progressCache[part] || 0;
      delete this.progressCache[part];
    }

    const inProgress = Object.keys(this.progressCache)
      .map(Number)
      .reduce((memo, id) => (memo += this.progressCache[id]), 0);

    const fileSize = this.file.size || 0;
    const sent = Math.min(this.uploadedSize + inProgress, fileSize);
    const total = fileSize;
    const percentage = Math.round((sent / total) * 100);

    this.onProgressFn({ sent, total, percentage });
  }

  upload(file: Blob, part: AWSPart, sendChunkStarted: () => void) {
    return new Promise<number>((resolve, reject) => {
      if (!this.uploadId || !this.fileKey) {
        reject(new Error('Upload not initialized'));
        return;
      }

      const xhr = new XMLHttpRequest();
      this.activeConnections[part.PartNumber - 1] = xhr;

      sendChunkStarted();

      const progressListener = this.handleProgress.bind(
        this,
        part.PartNumber - 1,
      );

      xhr.upload.addEventListener(UPLOAD_TYPES.PROGRESS, progressListener);
      xhr.addEventListener(UPLOAD_TYPES.ERROR, progressListener);
      xhr.addEventListener(UPLOAD_TYPES.ABORT, progressListener);
      xhr.addEventListener(UPLOAD_TYPES.LOADEND, progressListener);
      xhr.open('PUT', part.signedUrl);

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4 && xhr.status === 200) {
          const ETag = xhr.getResponseHeader('ETag');
          if (ETag) {
            const uploadedPart: UploadedPart = {
              PartNumber: part.PartNumber,
              ETag: ETag.replaceAll('"', ''),
            };

            this.uploadedParts.push(uploadedPart);
            resolve(xhr.status);
            delete this.activeConnections[part.PartNumber - 1];
          }
        }
      };

      xhr.onerror = (error) => {
        reject(error);
        delete this.activeConnections[part.PartNumber - 1];
      };

      xhr.onabort = () => {
        reject(new Error('Upload canceled by user'));
        delete this.activeConnections[part.PartNumber - 1];
      };

      xhr.send(file);
    });
  }

  onProgress(onProgress: (progress: ProgressData) => void) {
    this.onProgressFn = onProgress;
    return this;
  }

  onError(onError: (error: Error) => void) {
    this.onErrorFn = onError;
    return this;
  }

  onComplete(onComplete: () => void) {
    this.onCompleteFn = onComplete;
    return this;
  }

  abort() {
    Object.keys(this.activeConnections)
      .map(Number)
      .forEach((id) => {
        const connection = this.activeConnections[id];
        if (connection && typeof connection !== 'boolean') connection.abort();
      });

    this.aborted = true;
  }
}
